import React, { useCallback, useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, MessageSquare, Quote } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { adminApi } from "../lib/api";

export default function AdminFeedback() {
  const { t, locale } = useI18n();
  const [data, setData] = useState({ items: [], total: 0, up: 0, down: 0, with_case: 0 });
  const [rating, setRating] = useState("");
  const [onlyCase, setOnlyCase] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (rating) params.rating = rating;
      if (onlyCase) params.only_with_case = true;
      const { data } = await adminApi.feedback(params);
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [rating, onlyCase]);

  useEffect(() => {
    load();
  }, [load]);

  const satisfaction = data.total
    ? Math.round((data.up / data.total) * 100)
    : 0;

  return (
    <div data-testid="admin-feedback-page">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
          / retours citoyens
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">
          {locale === "fr" ? "Retours & cas partagés" : "Feedback & shared cases"}
        </h1>
        <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
          {locale === "fr"
            ? "Boucle de feedback anonyme : chaque réponse peut être notée, chaque citoyen peut partager son cas. Un canal d'observation précieux pour l'équipe qualité."
            : "Anonymous feedback loop: each answer can be rated, each citizen can share their case. A precious observation channel for the quality team."}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <MetricCard
          icon={MessageSquare}
          value={data.total}
          label={locale === "fr" ? "Retours totaux" : "Total feedback"}
          testid="metric-total"
        />
        <MetricCard
          icon={ThumbsUp}
          value={data.up}
          label={locale === "fr" ? "Utiles" : "Helpful"}
          tone="up"
          testid="metric-up"
        />
        <MetricCard
          icon={ThumbsDown}
          value={data.down}
          label={locale === "fr" ? "Peu utiles" : "Not helpful"}
          tone="down"
          testid="metric-down"
        />
        <MetricCard
          icon={Quote}
          value={data.with_case}
          label={locale === "fr" ? "Cas partagés" : "Shared cases"}
          tone="accent"
          testid="metric-with-case"
        />
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-end gap-2">
          <div className="font-display text-4xl font-semibold text-primary leading-none">
            {satisfaction}%
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 pb-1">
            {locale === "fr" ? "de satisfaction" : "satisfaction"}
          </div>
        </div>

        <div className="flex items-end gap-2 flex-wrap">
          <FilterPill
            active={rating === ""}
            onClick={() => setRating("")}
            label={locale === "fr" ? "Tous" : "All"}
            testid="filter-all"
          />
          <FilterPill
            active={rating === "up"}
            onClick={() => setRating("up")}
            label="👍"
            testid="filter-up"
          />
          <FilterPill
            active={rating === "down"}
            onClick={() => setRating("down")}
            label="👎"
            testid="filter-down"
          />
          <FilterPill
            active={onlyCase}
            onClick={() => setOnlyCase((v) => !v)}
            label={locale === "fr" ? "Avec cas" : "With case"}
            testid="filter-withcase"
          />
        </div>
      </div>

      {loading && (
        <div className="font-mono text-sm text-foreground/50 mb-3">{t("common.loading")}</div>
      )}

      <ul className="space-y-3" data-testid="feedback-list">
        {data.items.length === 0 && !loading && (
          <li
            className="bg-white border border-primary/10 rounded-sm p-10 text-center text-foreground/50 text-sm"
            data-testid="feedback-empty"
          >
            {locale === "fr" ? "Aucun retour pour ces filtres." : "No feedback for these filters."}
          </li>
        )}
        {data.items.map((f) => (
          <li
            key={f.id}
            className="bg-white border border-primary/15 rounded-sm p-5"
            data-testid={`feedback-item-${f.id}`}
          >
            <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
              <div className="flex items-center gap-2">
                {f.rating === "up" ? (
                  <span className="inline-flex items-center gap-1 bg-[#D1FAE5] text-[#065F46] px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-wider font-semibold">
                    <ThumbsUp size={11} />
                    {locale === "fr" ? "Utile" : "Helpful"}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-[#FEE2E2] text-[#991B1B] px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-wider font-semibold">
                    <ThumbsDown size={11} />
                    {locale === "fr" ? "Peu utile" : "Not helpful"}
                  </span>
                )}
                <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/50">
                  {f.language?.toUpperCase()}
                </span>
              </div>
              <span className="font-mono text-[10px] text-foreground/45">
                {new Date(f.created_at).toLocaleString()}
              </span>
            </div>
            <p className="text-sm text-foreground/85 font-medium mb-2">{f.question}</p>
            {f.case_text && (
              <blockquote className="border-l-2 border-accent pl-3 py-1 text-sm italic text-foreground/75 leading-relaxed font-body mt-2">
                « {f.case_text} »
              </blockquote>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MetricCard({ icon: Icon, value, label, tone, testid }) {
  const toneCls =
    tone === "up"
      ? "text-[#065F46]"
      : tone === "down"
      ? "text-[#991B1B]"
      : tone === "accent"
      ? "text-accent-dark"
      : "text-primary";
  return (
    <div
      className="bg-white border border-primary/15 rounded-sm p-5 hover:-translate-y-0.5 hover:shadow-md transition-all"
      data-testid={testid}
    >
      <div className="w-9 h-9 bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-3">
        <Icon size={15} />
      </div>
      <div className={`font-display text-3xl font-semibold leading-none ${toneCls}`}>
        {value}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-foreground/60 mt-2">
        {label}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center px-3 py-1.5 rounded-sm text-xs font-medium border transition-all ${
        active
          ? "bg-primary text-white border-primary"
          : "bg-white text-foreground/70 border-primary/20 hover:border-primary"
      }`}
    >
      {label}
    </button>
  );
}
